import * as Yup from "yup";
import { englishTestRanges } from "types";

type TestType = "TOEFL" | "IELTS" | "PTE" | "CAE" | "CPE";

const isValidScore = function (
  this: any,
  score: number | undefined | null,
  testType: TestType | ""
): boolean {
  return (
    !testType ||
    score === undefined ||
    score === null ||
    (score >= englishTestRanges[testType as TestType].min &&
      score <= englishTestRanges[testType as TestType].max)
  );
};

export const preScreeningSchema = [
  Yup.object({
    preferredLevel: Yup.string().required("Please choose a level."),
    preferredSubject: Yup.string().min(3).required("Please enter a subject."),
    preferredCountry: Yup.string().required("Please choose a country."),
  }),

  Yup.object({
    title: Yup.string().required("Please enter a title."),
    firstName: Yup.string().required("Please enter a first name."),
    lastName: Yup.string().when("isSingleNameOnly", {
      is: false,
      then: (schema) => schema.required("Please enter a last name."),
      otherwise: (schema) => schema.notRequired(),
    }),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    maritalStatus: Yup.string().required("Please choose a marital status."),
    dateOfMarriage: Yup.date()
      .nullable()
      .when("maritalStatus", {
        is: "Married",
        then: (schema) => schema.required("Please choose a date of marriage."),
      }),
  }),

  Yup.object({
    englishTest: Yup.string().required("Please choose a test type"),
    reading: Yup.number()
      .test(
        "score-range",
        "Invalid score for the selected test type",
        function (score) {
          return isValidScore.call(
            this,
            score,
            this.parent.englishTest as TestType
          );
        }
      )
      .required("Please enter a reading score."),
    writing: Yup.number()
      .test(
        "score-range",
        "Invalid score for the selected test type",
        function (score) {
          return isValidScore.call(
            this,
            score,
            this.parent.englishTest as TestType
          );
        }
      )
      .required("Please enter a writing score."),
    speaking: Yup.number()
      .test(
        "score-range",
        "Invalid score for the selected test type",
        function (score) {
          return isValidScore.call(
            this,
            score,
            this.parent.englishTest as TestType
          );
        }
      )
      .required("Please enter a speaking score."),
    listening: Yup.number()
      .test(
        "score-range",
        "Invalid score for the selected test type",
        function (score) {
          return isValidScore.call(
            this,
            score,
            this.parent.englishTest as TestType
          );
        }
      )
      .required("Please enter a listening score."),
    useOfEnglish: Yup.number()
      .nullable()
      .when("testType", {
        is: (testType: string) => testType === "CAE" || testType === "CPE",
        then: (schema) =>
          schema
            .test(
              "score-range",
              "Invalid score for the selected test type",
              function (score) {
                return isValidScore.call(
                  this,
                  score,
                  this.parent.englishTest as TestType
                );
              }
            )
            .required("Please enter a use of English score."),
        otherwise: (schema) => schema.nullable(),
      }),
    overallScore: Yup.number()
      .test(
        "score-range",
        "Invalid score for the selected test type",
        function (score) {
          return isValidScore.call(
            this,
            score,
            this.parent.englishTest as TestType
          );
        }
      )
      .required("Please enter an overall score."),
    testDate: Yup.date().required("Please choose a test date."),
    testReportFormNumber: Yup.string().required("Please enter report number."),
  }),

  Yup.object({
    highSchoolName: Yup.string().required("Please enter your high school name"),
    highSchoolCity: Yup.string().required("Please enter city"),
    highSchoolCountry: Yup.string().required("Please enter country"),
    highSchoolGradeObtained: Yup.string().required("Please enter a grade"),
    highSchoolStartDate: Yup.date().required("Please provide a start date"),
    highSchoolEndDate: Yup.date().when("highSchoolIsStillAttending", {
      is: false,
      then: (schema) => schema.required("Please provide an end date"),
      otherwise: (schema) => schema.nullable(),
    }),
    tertiary1School: Yup.string().required("Please enter institution name"),
    tertiary1Course: Yup.string().required("Please enter course name"),
    tertiary1City: Yup.string().required("Please enter city"),
    tertiary1Country: Yup.string().required("Please enter country"),
    tertiary1GradeObtained: Yup.string().required("Please enter a grade"),
    tertiary1StartDate: Yup.date().required("Please provide a start date"),
    tertiary1EndDate: Yup.date().when("tertiary1IsStillAttending", {
      is: false,
      then: (schema) => schema.required("Please provide an end date"),
      otherwise: (schema) => schema.nullable(),
    }),
    tertiary2School: Yup.string().test(
      "conditional-required",
      "Please enter institution name",
      function (value) {
        const {
          tertiary2Course,
          tertiary2City,
          tertiary2Country,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary2Course,
          tertiary2City,
          tertiary2Country,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      }
    ),
    tertiary2Course: Yup.string().test(
      "conditional-required",
      "Please enter course name",
      function (value) {
        const {
          tertiary2School,
          tertiary2City,
          tertiary2Country,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary2School,
          tertiary2City,
          tertiary2Country,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      }
    ),
    tertiary2City: Yup.string().test(
      "conditional-required",
      "Please enter city",
      function (value) {
        const {
          tertiary2School,
          tertiary2Course,
          tertiary2Country,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary2School,
          tertiary2Course,
          tertiary2Country,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      }
    ),
    tertiary2Country: Yup.string().test(
      "conditional-required",
      "Please enter country",
      function (value) {
        const {
          tertiary2School,
          tertiary2Course,
          tertiary2City,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary2School,
          tertiary2Course,
          tertiary2City,
          tertiary2GradeObtained,
          tertiary2StartDate,
          tertiary2EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      }
    ),
    tertiary2GradeObtained: Yup.string().test(
      "conditional-required",
      "Please enter a grade",
      function (value) {
        const {
          tertiary2School,
          tertiary2Course,
          tertiary2City,
          tertiary2Country,
          tertiary2StartDate,
          tertiary2EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary2School,
          tertiary2Course,
          tertiary2City,
          tertiary2Country,
          tertiary2StartDate,
          tertiary2EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      }
    ),
    tertiary2StartDate: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide a start date",
        function (value) {
          const {
            tertiary2School,
            tertiary2Course,
            tertiary2City,
            tertiary2Country,
            tertiary2GradeObtained,
            tertiary2EndDate,
          } = this.parent;
          const hasAnyValue = [
            tertiary2School,
            tertiary2Course,
            tertiary2City,
            tertiary2Country,
            tertiary2GradeObtained,
            tertiary2EndDate,
          ].some((val) => val && val !== "");
          return hasAnyValue ? !!value : true;
        }
      ),
    tertiary2EndDate: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide an end date",
        function (value) {
          const {
            tertiary2School,
            tertiary2Course,
            tertiary2City,
            tertiary2Country,
            tertiary2GradeObtained,
            tertiary2StartDate,
          } = this.parent;
          const hasAnyValue = [
            tertiary2School,
            tertiary2Course,
            tertiary2City,
            tertiary2Country,
            tertiary2GradeObtained,
            tertiary2StartDate,
          ].some((val) => val && val !== "");
          return hasAnyValue ? !!value : true;
        }
      ),

    tertiary3School: Yup.string().test(
      "conditional-required",
      "Please enter institution name",
      function (value) {
        const {
          tertiary3Course,
          tertiary3City,
          tertiary3Country,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary3Course,
          tertiary3City,
          tertiary3Country,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      }
    ),
    tertiary3Course: Yup.string().test(
      "conditional-required",
      "Please enter course name",
      function (value) {
        const {
          tertiary3School,
          tertiary3City,
          tertiary3Country,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary3School,
          tertiary3City,
          tertiary3Country,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      }
    ),
    tertiary3City: Yup.string().test(
      "conditional-required",
      "Please enter city",
      function (value) {
        const {
          tertiary3School,
          tertiary3Course,
          tertiary3Country,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary3School,
          tertiary3Course,
          tertiary3Country,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      }
    ),
    tertiary3Country: Yup.string().test(
      "conditional-required",
      "Please enter country",
      function (value) {
        const {
          tertiary3School,
          tertiary3Course,
          tertiary3City,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary3School,
          tertiary3Course,
          tertiary3City,
          tertiary3GradeObtained,
          tertiary3StartDate,
          tertiary3EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      }
    ),
    tertiary3GradeObtained: Yup.string().test(
      "conditional-required",
      "Please enter a grade",
      function (value) {
        const {
          tertiary3School,
          tertiary3Course,
          tertiary3City,
          tertiary3Country,
          tertiary3StartDate,
          tertiary3EndDate,
        } = this.parent;
        const hasAnyValue = [
          tertiary3School,
          tertiary3Course,
          tertiary3City,
          tertiary3Country,
          tertiary3StartDate,
          tertiary3EndDate,
        ].some((val) => val && val !== "");
        return hasAnyValue ? !!value : true;
      }
    ),
    tertiary3StartDate: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide a start date",
        function (value) {
          const {
            tertiary3School,
            tertiary3Course,
            tertiary3City,
            tertiary3Country,
            tertiary3GradeObtained,
            tertiary3EndDate,
          } = this.parent;
          const hasAnyValue = [
            tertiary3School,
            tertiary3Course,
            tertiary3City,
            tertiary3Country,
            tertiary3GradeObtained,
            tertiary3EndDate,
          ].some((val) => val && val !== "");
          return hasAnyValue ? !!value : true;
        }
      ),
    tertiary3EndDate: Yup.date()
      .nullable()
      .test(
        "conditional-required",
        "Please provide an end date",
        function (value) {
          const {
            tertiary3School,
            tertiary3Course,
            tertiary3City,
            tertiary3Country,
            tertiary3GradeObtained,
            tertiary3StartDate,
          } = this.parent;
          const hasAnyValue = [
            tertiary3School,
            tertiary3Course,
            tertiary3City,
            tertiary3Country,
            tertiary3GradeObtained,
            tertiary3StartDate,
          ].some((val) => val && val !== "");
          return hasAnyValue ? !!value : true;
        }
      ),

    currentlyStudyingAt: Yup.string().nullable(),
  }),
];
